import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, SITE_ACCESS_KEY } from "../lib/api";
import { Lock, AlertCircle } from "lucide-react";

/**
 * Password gate per l'intera app (beta privata).
 * - Se in localStorage c'è già un token valido, mostra direttamente i children.
 * - Altrimenti mostra una schermata con campo password.
 * - Il token rilasciato dal backend (HMAC firmato, 30g) viene allegato a tutte
 *   le richieste tramite l'interceptor in lib/api.js (header X-Site-Access).
 */
export default function BetaGate({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(SITE_ACCESS_KEY));
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(true);

  // Al primo mount:
  // 1) Probe sul backend per capire se il gate è disabilitato (SITE_PASSWORD vuoto).
  // 2) Se ho un token in storage, lo verifico contro /api/routes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // 1) probe: il backend restituisce {disabled:true} se SITE_PASSWORD è vuoto
      try {
        const { data } = await axios.post(`${API}/site-access`, { password: "" }, {
          validateStatus: () => true,
        });
        if (!cancelled && data?.disabled && data?.token) {
          localStorage.setItem(SITE_ACCESS_KEY, data.token);
          setToken(data.token);
          setVerifying(false);
          return;
        }
      } catch { /* ignora — riprova con token in storage */ }

      // 2) se ho un token, verifico che sia ancora valido contro un endpoint gated
      const stored = localStorage.getItem(SITE_ACCESS_KEY);
      if (!stored) {
        if (!cancelled) setVerifying(false);
        return;
      }
      try {
        const r = await axios.get(`${API}/routes`, {
          headers: { "X-Site-Access": stored },
          validateStatus: () => true,
        });
        if (!cancelled && r.status === 401 && r.data?.code === "SITE_ACCESS_REQUIRED") {
          localStorage.removeItem(SITE_ACCESS_KEY);
          setToken(null);
        }
      } catch { /* offline → lascia il token */ }
      if (!cancelled) setVerifying(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/site-access`, { password });
      localStorage.setItem(SITE_ACCESS_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Errore di connessione");
    } finally {
      setBusy(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-parchment-grain flex items-center justify-center">
        <div className="text-ink-500 font-bold">Verifica accesso…</div>
      </div>
    );
  }

  if (token) return children;

  return (
    <div className="min-h-screen bg-parchment-grain flex items-center justify-center p-4" data-testid="beta-gate">
      <div className="card-treasure p-8 max-w-md w-full">
        <div className="flex flex-col items-center mb-6">
          <img
            src="/odismap-logo-transparent.png"
            alt="Odismap"
            className="h-24 w-auto mb-3"
            draggable={false}
            data-testid="beta-gate-logo"
          />
          <p className="text-ink-500 text-sm font-bold uppercase tracking-widest">Accesso riservato</p>
        </div>
        <div className="flex items-center gap-2 p-3 bg-gold-light border border-gold/30 rounded-2xl mb-5 text-sm text-ink-800">
          <Lock size={18} className="text-gold-hover shrink-0"/>
          <div><strong>Sito in beta privata.</strong> Inserisci la password fornita dal team Odismap per accedere.</div>
        </div>
        <form onSubmit={submit} className="space-y-3" data-testid="beta-gate-form">
          <label className="text-ink-800 font-bold text-sm">Password beta</label>
          <input
            type="password"
            autoFocus
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••••••"
            className="h-12 w-full px-4 rounded-2xl border border-outline bg-white focus:outline-none focus:ring-2 focus:ring-gold"
            data-testid="beta-gate-password-input"
          />
          {error && (
            <div className="flex items-center gap-2 text-sm text-compass bg-red-50 p-3 rounded-xl border border-red-200" data-testid="beta-gate-error">
              <AlertCircle size={16}/> {error}
            </div>
          )}
          <button type="submit" disabled={busy} className="btn-tactile w-full" data-testid="beta-gate-submit">
            {busy ? "Verifica…" : "Sblocca"}
          </button>
        </form>
      </div>
    </div>
  );
}
